import {Component, inject} from '@angular/core';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatRadioModule} from '@angular/material/radio';
import {MatButtonModule} from '@angular/material/button';
import {ApiService} from '@src/app/shared/services/api.service';
import {AuthService} from '@src/app/core/services/auth.service';
import {SettingsService} from '@src/app/core/services/settings.service';
import {Services} from '@src/app/core/enums/services.enum';
import {of, switchMap} from 'rxjs';

@Component({
    selector: 'app-experience-settings',
    imports: [ReactiveFormsModule, MatDialogModule, MatCheckboxModule, MatRadioModule, MatButtonModule],
    template: `
        <div mat-dialog-title class="app-dialog-header">
            <h2 class="app-dialog-title">Tailor your experience</h2>
        </div>
        <form [formGroup]="form" (ngSubmit)="save()">
            <mat-dialog-content class="experience-content">
                <fieldset>
                    <legend>Surveying services</legend>
                    <p class="section-hint">Choose one or both.</p>
                    <div class="choices">
                        @for (service of services; track service.id) {
                            <mat-checkbox class="choice" color="primary" [class.selected]="form.controls.enabled_services.value.includes(service.id)" [checked]="form.controls.enabled_services.value.includes(service.id)" (change)="toggle(service.id, $event.checked)" [disabled]="saving">{{ service.label }}</mat-checkbox>
                        }
                    </div>
                    @if (submitted && form.controls.enabled_services.invalid) { <p class="validation-error" role="alert">Choose at least one service.</p> }
                </fieldset>
                <fieldset>
                    <legend>Product pricing</legend>
                    <p class="section-hint" id="pricing-explanation">Add product rates and track costs across your surveys. Hide pricing if you only need inspections and reports. This won’t affect your subscription or credits.</p>
                    <mat-radio-group class="choices" formControlName="pricing_enabled" aria-label="Product pricing" aria-describedby="pricing-explanation">
                        <mat-radio-button class="choice" color="primary" [class.selected]="form.controls.pricing_enabled.value === true" [value]="true" [disabled]="saving">Show pricing</mat-radio-button>
                        <mat-radio-button class="choice" color="primary" [class.selected]="form.controls.pricing_enabled.value === false" [value]="false" [disabled]="saving">Hide pricing</mat-radio-button>
                    </mat-radio-group>
                    @if (submitted && form.controls.pricing_enabled.invalid) { <p class="validation-error" role="alert">Choose whether to show pricing.</p> }
                </fieldset>
                <fieldset>
                    <legend>Product updates</legend>
                    <p class="section-hint">Optional. You can change this anytime in your preferences.</p>
                    <mat-checkbox color="primary" formControlName="marketing_consent" [disabled]="saving">Email me product updates and offers. <a [href]="privacyPolicyUrl" target="_blank" rel="noopener noreferrer" (click)="$event.stopPropagation()">Privacy Policy</a>.</mat-checkbox>
                </fieldset>
                <p class="settings-note">Change these anytime in Settings. Existing work stays available.</p>
                @if (error) { <p class="validation-error" role="alert">{{ error }}</p> }
            </mat-dialog-content>
            <mat-dialog-actions align="end" class="experience-actions">
                <button mat-flat-button color="primary" type="submit" [disabled]="saving">{{ saving ? 'Saving…' : 'Save choices' }}</button>
            </mat-dialog-actions>
        </form>
    `,
    styles: [`
        .app-dialog-title { margin: 0; font-size: 1.15rem; line-height: 1.3; }
        .experience-content { padding: 24px; max-height: calc(100dvh - 160px); }
        fieldset { border: 0; padding: 0; margin: 0 0 24px; min-width: 0; }
        legend { float: none; width: auto; margin: 0 0 4px; font-size: 1rem; font-weight: 600; text-transform: none; color: inherit; }
        .section-hint, .settings-note { font-size: .875rem; line-height: 1.5; }
        .section-hint { margin: 0 0 12px; }
        .choices { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
        .choice { min-width: 0; border: 1px solid color-mix(in srgb, currentColor 25%, transparent); border-radius: 4px; }
        .choice.selected { border-color: var(--brand-primary); background: rgba(var(--brand-primary-rgb), .08); }
        .choice:focus-within { border-color: var(--brand-primary); }
        :host ::ng-deep .choice .mdc-form-field { display: flex; width: 100%; min-height: 48px; }
        :host ::ng-deep .choice .mdc-label { display: flex; align-items: center; align-self: stretch; flex: 1; padding-right: 12px; cursor: pointer; }
        .settings-note { margin: 0; }
        .validation-error { color: var(--brand-warn); font-size: .875rem; margin: 8px 0 0; }
        .experience-actions { padding: 0 24px 24px; gap: 8px; }
        @media (max-width: 359px) { .choices { grid-template-columns: 1fr; gap: 8px; } }
    `],
})
export class ExperienceSettingsComponent
{
    private readonly api = inject(ApiService);
    private readonly auth = inject(AuthService);
    private readonly settings = inject(SettingsService);
    private readonly dialog = inject(MatDialogRef<ExperienceSettingsComponent>);
    public readonly privacyPolicyUrl = 'https://firesurveypro.co.uk/privacy-policy';
    public readonly services = [{id: Services.FireStopping, label: 'Fire stopping'}, {id: Services.FireDoors, label: 'Fire doors'}];
    public readonly form = inject(FormBuilder).group({
        enabled_services: [[] as string[], Validators.required],
        pricing_enabled: [null as boolean | null, control => typeof control.value === 'boolean' ? null : {required: true}],
        marketing_consent: [this.auth.user?.marketing_consent === true],
    });
    public submitted = false;
    public saving = false;
    public error = '';

    public toggle(id: string, checked: boolean): void
    {
        const selected = this.form.controls.enabled_services;
        selected.setValue(checked ? [...selected.value, id] : selected.value.filter(value => value !== id));
    }

    public save(): void
    {
        this.submitted = true;
        if (this.saving || this.form.invalid) { return; }
        this.error = '';
        this.saving = true;
        const {marketing_consent, ...choices} = this.form.getRawValue();
        const consent = marketing_consent === true;
        const consentRequest = !this.auth.hasAnsweredMarketingConsent() || this.auth.user?.marketing_consent !== consent
            ? this.auth.updateMarketingConsent(consent)
            : of(null);
        consentRequest.pipe(switchMap(() => this.api.updateSettings('settings/services', choices))).subscribe({
            next: response => { this.settings.hydrate(response.data); this.dialog.close(true); },
            error: () => { this.saving = false; this.error = 'We couldn’t save your choices. Please try again.'; },
        });
    }
}
